// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import React, { useState, useEffect, useMemo } from "react";
import { connect, useSelector } from "react-redux";

import { Dock as StyledDock, Content } from "../styles";
import ChecklistSection from "../Checklist";
import LoadingState from "src/components/LoadingState";
import NoFields from "./NoFields";

import {
  RefreshWrapper,
  RefreshMessage,
  RefreshButton,
  ChecklistWrapper,
} from "./styles";
import {
  getCurrentChecklist,
  getIsSrwMobile,
  getChecklistLoaderState,
  getFieldsByChecklistId,
  getCurrentRoom,
  getCurrentUserId,
  getCurrentRoomId,
} from "src/reducers";
import { makeGetRecentChats } from "src/reducers/recentChats";
import { getChatroomChecklistFields } from "src/actions/checklist";
import * as notifications from "src/constants/notifications";

import type { AppState } from "src/types";

type Props = {
  checklists: any,
  fetchingFields: boolean,
  _getChatroomChecklistFields: Function,
};

const Checklist = ({
  checklists,
  fetchingFields,
  _getChatroomChecklistFields,
}: Props) => {
  const [refreshOption, setRefreshOption] = useState(false);
  const isSrwMobile = useSelector(({ app }) => getIsSrwMobile(app));
  const roomId = useSelector(({ app }) => getCurrentRoom(app));

  // $FlowFixMe - Optional chaining is not yet supported
  const currentChecklistId = checklists?.[0]?.id;
  const fieldsByChecklist = useSelector(({ app }) =>
    getFieldsByChecklistId(app, currentChecklistId),
  );

  const checklistFieldCount = fieldsByChecklist?.length ?? 0;

  // Show Refresh button after 3 minutes of clicking refresh
  let refreshTimeout;
  const showRefreshButton = () => {
    const timeoutId = setTimeout(() => {
      setRefreshOption(true);
    }, 180000);

    if (refreshTimeout) {
      clearTimeout(timeoutId);
    }
  };

  useEffect(() => {
    // Show Refresh button after 3 minutes of initial checklist load
    showRefreshButton();
    setPreventInitialRender(false);
  }, []);

  // Reload checklist on clicking refresh button and hide button
  const reloadChecklist = () => {
    checklists.map((el) => {
      _getChatroomChecklistFields(el.id, roomId);
    });
    showRefreshButton();
    setRefreshOption(false);
  };

  const room = useSelector(({ app }) => getCurrentRoomId(app));
  const [preventInitialRender, setPreventInitialRender] = useState(true);
  const currentUser = useSelector(({ app }) => getCurrentUserId(app));

  const getRecentChats = useMemo(() => makeGetRecentChats(), []);
  const recentChats = useSelector(({ app }) =>
    getRecentChats(app.recentChats, room),
  );

  // Checks if last message notification is from checklist actions
  const isLastMessageChecklist =
    recentChats[recentChats.length - 1]?.action ===
    notifications.CHECKLIST_FIELD_FILL;

  const isAuthorAndCurrentUserSame = isLastMessageChecklist
    ? currentUser === recentChats[recentChats.length - 1]?.author
    : false;

  const data = recentChats[recentChats.length - 1]?.data;

  // Shows refresh option if update in checklist ignoring initial load.
  useEffect(() => {
    if (
      !preventInitialRender &&
      isLastMessageChecklist &&
      !isAuthorAndCurrentUserSame
    ) {
      if (!refreshOption) {
        setRefreshOption(true);
        showRefreshButton();
      }
    }
  }, [data]);

  useEffect(() => {
    if (currentChecklistId) {
      _getChatroomChecklistFields(currentChecklistId, roomId);
    }
  }, [currentChecklistId, roomId]);

  return (
    <StyledDock>
      <Content data-cy="checklist-dock">
        <ChecklistWrapper>
          {isSrwMobile && refreshOption && (
            <RefreshWrapper>
              <RefreshMessage>
                <RefreshButton onClick={reloadChecklist}>
                  {i18n.t(k.REFRESH)}
                </RefreshButton>
                {i18n.t(k.TO_SEE_LATEST_CHECKLIST_DATA)}
              </RefreshMessage>
            </RefreshWrapper>
          )}

          {fetchingFields && <LoadingState type="panelBody" />}
          {!fetchingFields && checklistFieldCount === 0 && (
            <NoFields roomId={roomId} />
          )}
          {!fetchingFields && checklistFieldCount > 0 && (
            <ChecklistSection
              checklistId={currentChecklistId}
              roomId={roomId}
            />
          )}
        </ChecklistWrapper>
      </Content>
    </StyledDock>
  );
};

const mapStateToProps = ({ app }: { app: AppState }) => ({
  checklists: getCurrentChecklist(app),
  fetchingFields: getChecklistLoaderState(app),
});

export default connect(mapStateToProps, {
  _getChatroomChecklistFields: getChatroomChecklistFields,
})(Checklist);
